/**
 * The radar at the selected point: one `/api/now` reading, kept current.
 *
 * Its own refresher rather than a part of the forecast bundle's: the bundle is
 * five minutes old by design and the radar frame is two, and the reading is
 * the one thing in the hero that changes on the scale of a rain edge crossing
 * the point. Asked when the point changes, every two minutes while visible,
 * and at once on return (`whileVisible`).
 */

import { untrack } from 'svelte'

import { ApiError, fetchNow } from '../api/client'
import type { ForecastBundle, NowReading } from '../api/types'
import { radarOverride } from '../forecast/now'
import { whileVisible } from '../visibility'
import { location } from './location.svelte'

/** MRMS publishes every two minutes. */
export const RADAR_REFRESH_MS = 2 * 60_000
/** How often a visible page checks that age, and moves `clock`. */
const CHECK_MS = 30_000

export const radar = $state({
  reading: null as NowReading | null,
  /** The point `reading` is about; null when there is none. */
  for: null as { lat: number; lon: number } | null,
  /** When the last ask ended, answered or not, so a failure is not re-asked
   *  every tick: a deployment with the radar off answers 404 every time. */
  askedAt: 0,
  /**
   * The time freshness is judged against, moved on every check. Without it a
   * page left open with the server gone keeps a frame from an hour ago,
   * because nothing that a `$derived` reads would ever change.
   */
  clock: Date.now(),
})

let seq = 0
let controller: AbortController | null = null

function isFor(point: { lat: number; lon: number } | null | undefined): boolean {
  return !!point && radar.for?.lat === point.lat && radar.for?.lon === point.lon
}

export async function refreshRadar(): Promise<void> {
  radar.clock = Date.now()
  const point = location.selected
  if (!point) return
  if (isFor(point) && Date.now() - radar.askedAt < RADAR_REFRESH_MS) return
  controller?.abort()
  const mine = new AbortController()
  controller = mine
  const ask = ++seq
  const { lat, lon } = point
  let reading: NowReading | null = null
  try {
    reading = await fetchNow(lat, lon, mine.signal)
  } catch (e) {
    if (ask !== seq) return
    // Off (404) and "no frame yet" (503) are answers; anything else is worth
    // a line, because the hero then quietly falls back to the station.
    if (!(e instanceof ApiError && (e.status === 404 || e.status === 503))) {
      console.warn('radar reading failed; the station speaks alone', e)
    }
  }
  if (ask !== seq) return
  radar.reading = reading
  radar.for = { lat, lon }
  radar.askedAt = Date.now()
  radar.clock = radar.askedAt
}

/**
 * The radar's reading when it should replace the station's words for this
 * bundle's point; null otherwise. A header passes `selectedBundle()`, so a
 * reading for the point looked at before never colours this one.
 */
export function radarOverrideFor(own: ForecastBundle | null): NowReading | null {
  if (!own || !isFor(location.selected)) return null
  return radarOverride(own.current, radar.reading, radar.clock)
}

/**
 * The reading for the point on screen, whatever the station says: the next
 * hour is the radar's alone. Null while a new point's first answer is on its
 * way, so the last point's hour is never drawn under this one's name.
 */
export function radarHere(): NowReading | null {
  return isFor(location.selected) ? radar.reading : null
}

/** Keep the reading current for as long as the page is open. Returns a disposer. */
export function startRadarRefresh(): () => void {
  const stopTimer = whileVisible(() => void refreshRadar(), CHECK_MS)
  const stopEffect = $effect.root(() => {
    $effect(() => {
      // Only the point is tracked; what `refreshRadar` reads of its own state
      // would otherwise re-run this on every answer.
      const point = location.selected
      if (point) untrack(() => void refreshRadar())
    })
  })
  return () => {
    stopTimer()
    stopEffect()
    controller?.abort()
  }
}

/** Test seam. */
export function _resetRadar(): void {
  controller?.abort()
  controller = null
  seq++
  radar.reading = null
  radar.for = null
  radar.askedAt = 0
  radar.clock = Date.now()
}
